import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import SpeciesSelect from '../../common/controls/SpeciesSelect';
import DatePicker from '../../common/controls/DatePicker';
import GenderRadioGroup from '../../common/controls/GenderRadioGroup';
import { useSavePet } from '../../../hooks/patients/useSavePet';
import { FormControlLabel, Switch } from '@mui/material';
import { useAuth } from '../../../hooks/useAuth';

export default function PetForm({ successCallback, pet = null, petOwner= null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 
    const { role, isAuthenticated } = useAuth();

    const [name, setName] = React.useState(pet ? pet.name : "");
    const [speciesId, setSpeciesId] = React.useState(pet ? pet.species_id : "");
    const [species, setSpecies] = React.useState(pet ? pet.species : "");
    const [birthdate, setBirthdate] = React.useState(pet ? pet.birthdate : "");
    const [defaultDate, setDefaultDate] = React.useState(pet ? pet.birthdate : "");
    const [neutered, setNeutered] = React.useState(pet ? !!pet.neutered : false);
    const [neuteredDate, setNeuteredDate] = React.useState(pet ? pet.neutered_date : "");
    const [deceased, setDeceased] = React.useState(pet ? !!pet.deceased : false);
    const [defaultNeuteredDate, setDefaultNeuteredDate] = React.useState(pet ? pet.neutered_date : "");
    const [breed, setBreed] = React.useState(pet ? pet.breed : "");
    const [gender, setGender] = React.useState(pet ? pet.gender : "");
    const [microchipSn, setMicrochipSn] = React.useState(pet && pet.microchip_sn ? pet.microchip_sn : "");

    const otherSpeciesRef = React.useRef(null);

    // Focus "Other Species" when speciesId becomes 7 
    React.useEffect(() => { 
        if (speciesId === 7 && otherSpeciesRef.current) { 
            otherSpeciesRef.current.focus(); 
        } 
    }, [speciesId]);

    const { create, update } = useSavePet(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(pet ? pet.name : "");
        setSpeciesId(pet && pet.species_id ? pet.species_id : "");
        setSpecies(pet && pet.species ? pet.species : "");
        setBirthdate(pet ? pet.birthdate : "");
        setNeutered(pet ? !!pet.neutered : false);
        setNeuteredDate(pet ? pet.neutered_date : "");
        setDeceased(pet ? !!pet.deceased : false);
        setDefaultDate(pet ? pet.birthdate : "");
        setDefaultNeuteredDate(pet ? pet.neutered_date : "");
        setBreed(pet ? pet.breed : "");
        setGender(pet ? pet.gender : "");
        setMicrochipSn(pet && pet.microchip_sn ? pet.microchip_sn : "");
    }, [pet, open]);

    const handleBirthDateChange = (date) => {
        if (date && date.formatted) {
            setBirthdate(date.formatted);
        }
    };

    const handleNeuteredDateChange = (date) => {
        if (date && date.formatted) {
            setNeuteredDate(date.formatted);
        }
    };

    const handleSpecisSelectChange = (e) => {
        setSpeciesId(e.target.value);
        if (e.target.value !== 7) {
            setSpecies("");
        }
    }

    const handleGenderChange = (e) => {
        setGender(e.target.value);
    }

    const handleSave = async () => {
        if (!name.trim()) {
            showSnackbar(t('pets.messages.name_required'), "warning");
            return;
        }
        if (!speciesId) {
            showSnackbar(t('pets.messages.species_required'), "warning");
            return;
        }
        if (speciesId === 7 && !species) {
            showSnackbar(t('pets.messages.other_species_required'), "warning");
            return;
        }

        try 
        {
            if (mode === "create") {
                await create({ 
                    pet_owner_id: petOwner.id,
                    name: name,
                    species_id: speciesId,
                    species: species,
                    birthdate: birthdate,
                    neutered: neutered,
                    neutered_date: !neutered? null: neuteredDate,
                    deceased: deceased,
                    breed: breed,
                    gender: gender,
                    microchip_sn: microchipSn
                });
                showSnackbar(t('pets.messages.create_success'), "success");
            } else if (mode === "edit" && pet) {
                await update(pet.id, { 
                    name: name,
                    species_id: speciesId,
                    species: species,
                    birthdate: birthdate,
                    neutered: neutered,
                    neutered_date: !neutered? null: neuteredDate,
                    deceased: deceased,
                    breed: breed,
                    gender: gender,
                    microchip_sn: microchipSn
                 });
                showSnackbar(t('pets.messages.update_success'), "success");
            }
            // clear field after save
            setName("");
            setSpeciesId("");
            setSpecies("");
            setBirthdate("");
            setNeutered(false);
            setNeuteredDate("");
            setDeceased(false);
            setDefaultDate("");
            setDefaultNeuteredDate("");
            setBreed("");
            setGender("");
            setMicrochipSn("");
            // close dialog
            onClose();     
        } catch (err) {
            showSnackbar(t('pets.messages.save_fail'), "error");
        }

    };

    const handleCancel = () => {
         // clear field after save
        setName("");
        setSpeciesId("");
        setSpecies("");
        setBirthdate("");
        setNeutered(false);
        setNeuteredDate("");
        setDeceased(false);
        setDefaultNeuteredDate("");
        setDefaultDate("");
        setBreed("");
        setMicrochipSn("");
        // close dialog
        onClose();     
    };

    return (
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>
        <DialogTitle>{mode === "create" ? "Add Pet" : "Edit Pet"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <TextField
                label="Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
            />
            <SpeciesSelect 
                value={speciesId}
                onChange={handleSpecisSelectChange}
            />
            {speciesId === 7 ?
            <TextField
                inputRef={otherSpeciesRef}
                disabled={speciesId !== 7} // disabled if species is not 'other'
                label="Other Species"
                variant="outlined"
                value={species}
                onChange={(e) => setSpecies(e.target.value)}
                fullWidth
            />
            : null }
            <TextField
                label="Breed"
                variant="outlined"
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                fullWidth
            />
            <GenderRadioGroup value={gender} onChange={handleGenderChange} />
            <DatePicker 
                defaultValue={defaultDate} 
                onChange={handleBirthDateChange} 
                label="Birthdate"
                restrict="past" 
                minYear={new Date().getFullYear() - 30}
                monthFormat="short"
                size="small" 
                order={["year", "month", "day"]}
                gap={0.5}
                controlWidths={{ month: 2, day: 2, year: 8 }}
                placeholders={{ month: "Month", day: "Day", year: "Year" }}
            />
            <FormControlLabel
                control={
                    <Switch
                    checked={neutered}
                    onChange={(e) => setNeutered(e.target.checked)}
                    color="primary"
                    />
                }
                label="Neutered"
            />
            {neutered ? 
            <DatePicker 
                defaultValue={defaultNeuteredDate} 
                onChange={handleNeuteredDateChange} 
                label="Neutered Date"
                restrict="past" 
                minYear={new Date().getFullYear() - 30}
                monthFormat="short"
                size="small" 
                order={["year", "month", "day"]}
                gap={0.5}
                controlWidths={{ month: 2, day: 2, year: 8 }}
                placeholders={{ month: "Month", day: "Day", year: "Year" }}
            />
            : null}
            <TextField
                label="Microchip SN"
                variant="outlined"
                value={microchipSn}
                onChange={(e) => setMicrochipSn(e.target.value)}
                fullWidth
            />
            {role !== 'guest' ?
            <FormControlLabel
                control={
                    <Switch
                    checked={deceased}
                    onChange={(e) => setDeceased(e.target.checked)}
                    color="primary"
                    />
                }
                label="Deceased"
            />
            : null }
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>Save</Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
    );
}